import React from "react";
import PropTypes from "prop-types";
import Link from "gatsby-link";
import Helmet from "react-helmet";
import classnames from "classnames";
import { Search } from "components/Search";
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  CubeIcon,
  LinkIcon,
  ArrowDownTrayIcon,
  TagIcon,
  VariableIcon,
  SunIcon,
  MoonIcon
} from "components/Icons";

import "normalize.css";
import "./reset.css";
import "./index.css";

const HEADER_HEIGHT = "64px";
const SIDEBAR_WIDTH = "260px";
const SIDEBAR_BG_COLOR = "#ffffff";

// GraphQL API contexts for organizing queries and mutations
const API_CONTEXTS = [
  "Entities", "Accounts", "Clinical", "Conversations", "Tasks",
  "Schedule", "Billing", "Triggers", "Reminders", "Inventory",
  "Lab", "Notifications", "Board", "Boarding", "Comments", "Events",
  "Hospital", "NewBilling", "Payroll", "Plan", "Marketing", "Rdvms",
  "Analytics", "Integrations", "License", "Telemedicine", "Treatment", "VoiceCall"
];

const SidebarSection = ({ title, items, currentPath, collapsed, onToggle, icon, count }) => {
  return (
    <div className="Sidebar-section">
      <div
        className="Sidebar-section-header"
        onClick={onToggle}
        style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {icon && <span style={{ display: "flex", alignItems: "center" }}>{icon}</span>}
          {title}
          {count !== undefined && (
            <span style={{
              fontSize: "1.1rem",
              backgroundColor: "rgba(77, 101, 255, 0.1)",
              color: "#4d65ff",
              padding: "2px 6px",
              borderRadius: "10px",
              fontWeight: "600"
            }}>
              {count}
            </span>
          )}
        </span>
        <span style={{ fontSize: "1.2rem", marginLeft: "4px" }}>{collapsed ? "▸" : "▾"}</span>
      </div>
      {!collapsed && items}
    </div>
  );
};

const NestedSidebarSection = ({ title, contexts, queryGroups, mutationGroups, currentPath, collapsed, onToggle }) => {
  const hasQueries = contexts.some(ctx => queryGroups[ctx] && queryGroups[ctx].length > 0);
  const hasMutations = contexts.some(ctx => mutationGroups[ctx] && mutationGroups[ctx].length > 0);

  if (!hasQueries && !hasMutations) return null;

  const totalCount = contexts.reduce((sum, ctx) => {
    const queryLength = queryGroups[ctx] && queryGroups[ctx].length ? queryGroups[ctx].length : 0;
    const mutationLength = mutationGroups[ctx] && mutationGroups[ctx].length ? mutationGroups[ctx].length : 0;
    return sum + queryLength + mutationLength;
  }, 0);

  return (
    <div className="Sidebar-section nested">
      <div
        className="Sidebar-section-header"
        onClick={onToggle}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: "700",
          fontSize: "1.35rem"
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {title}
          <span style={{
            fontSize: "1.1rem",
            backgroundColor: "rgba(77, 101, 255, 0.1)",
            color: "#4d65ff",
            padding: "2px 6px",
            borderRadius: "10px",
            fontWeight: "600"
          }}>
            {totalCount}
          </span>
        </span>
        <span style={{ fontSize: "1.2rem" }}>{collapsed ? "▸" : "▾"}</span>
      </div>
      {!collapsed && (
        <div style={{ paddingLeft: "12px" }}>
          {hasQueries && (
            <div style={{ marginTop: "8px" }}>
              <div style={{
                fontSize: "1.2rem",
                fontWeight: "600",
                color: "#10b981",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: "6px"
              }}>
                Queries
              </div>
              {contexts
                .filter(ctx => queryGroups[ctx] && queryGroups[ctx].length > 0)
                .flatMap(ctx =>
                  queryGroups[ctx].map(node => (
                    <Link
                      key={node.path}
                      className={currentPath === node.path ? "active" : ""}
                      to={node.path}
                      style={{ fontSize: "1.35rem" }}
                    >
                      {node.name}
                    </Link>
                  ))
                )
              }
            </div>
          )}
          {hasMutations && (
            <div style={{ marginTop: "8px" }}>
              <div style={{
                fontSize: "1.2rem",
                fontWeight: "600",
                color: "#f59e0b",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: "6px"
              }}>
                Mutations
              </div>
              {contexts
                .filter(ctx => mutationGroups[ctx] && mutationGroups[ctx].length > 0)
                .flatMap(ctx =>
                  mutationGroups[ctx].map(node => (
                    <Link
                      key={node.path}
                      className={currentPath === node.path ? "active" : ""}
                      to={node.path}
                      style={{ fontSize: "1.35rem" }}
                    >
                      {node.name}
                    </Link>
                  ))
                )
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
};

class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      collapsedSections: {},
      showSearch: false,
      darkMode: false
    };
    this.listenerAttached = false;

    // Bind handler in constructor to maintain reference
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      this.toggleSearch();
    }
  }

  toggleSection = (sectionName) => {
    this.setState(prev => ({
      collapsedSections: {
        ...prev.collapsedSections,
        [sectionName]: !prev.collapsedSections[sectionName]
      }
    }));
  };

  toggleSearch = () => {
    this.setState(prev => ({ showSearch: !prev.showSearch }));
  };

  toggleDarkMode = () => {
    this.setState(prev => {
      const newDarkMode = !prev.darkMode;
      if (newDarkMode) {
        document.body.classList.add("dark-mode");
        localStorage.setItem("darkMode", "true");
      } else {
        document.body.classList.remove("dark-mode");
        localStorage.setItem("darkMode", "false");
      }
      return { darkMode: newDarkMode };
    });
  };

  componentDidMount() {
    // Check for saved dark mode preference
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    if (savedDarkMode) {
      document.body.classList.add("dark-mode");
      this.setState({ darkMode: true });
    }

    // Add keyboard shortcut for search (Cmd/Ctrl + K) - only once
    if (!this.listenerAttached) {
      document.addEventListener("keydown", this.handleKeyDown);
      this.listenerAttached = true;
    }
  }

  componentWillUnmount() {
    if (this.listenerAttached) {
      document.removeEventListener("keydown", this.handleKeyDown);
      this.listenerAttached = false;
    }
  }

  render() {
    const { currentPath, pages } = this.props;
    const { collapsedSections, showSearch, darkMode } = this.state;

    // Get all contexts
    const contexts = API_CONTEXTS;

    const queryGroups = {};
    const mutationGroups = {};

    contexts.forEach(ctx => {
      queryGroups[ctx] = pages.filter(p => p.name === `${ctx}Queries`);
      mutationGroups[ctx] = pages.filter(p => p.name === `${ctx}Mutations`);
    });

    // Count total queries and mutations
    const totalQueries = contexts.reduce((sum, ctx) => sum + (queryGroups[ctx] ? queryGroups[ctx].length : 0), 0);
    const totalMutations = contexts.reduce((sum, ctx) => sum + (mutationGroups[ctx] ? mutationGroups[ctx].length : 0), 0);

    return (
      <div
        className="Sidebar"
        style={{
          paddingTop: "70px",
          width: SIDEBAR_WIDTH,
          backgroundColor: darkMode ? "#161b22" : SIDEBAR_BG_COLOR,
          height: "100%",
          zIndex: 10,
          position: "absolute",
          borderRight: `1px solid ${darkMode ? "#30363d" : "#e5e7eb"}`,
          boxShadow: "1px 0 3px rgba(0, 0, 0, 0.03)",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <div
          className="Header-sidebar"
          style={{
            width: "100%",
            height: "64px",
            backgroundColor: darkMode ? "#161b22" : SIDEBAR_BG_COLOR,
            display: "flex",
            alignItems: "center",
            padding: "5px 20px",
            fontWeight: "600",
            fontSize: "1.9rem",
            flexShrink: 0,
            position: "absolute",
            top: 0,
            left: 0,
            borderBottom: `1px solid ${darkMode ? "#30363d" : "#e5e7eb"}`,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
          }}
        >
          <a href="https://www.vetspire.ai" style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none", whiteSpace: "nowrap" }}>
            <svg width="166" height="32" viewBox="0 0 166 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: "28px", width: "auto", flexShrink: 0 }}>
              <path fillRule="evenodd" clipRule="evenodd" d="M0 10.8547L3.97554 0L12.839 11.4505L7.41762 19.6632L0 10.8547ZM12.8428 11.4505L18.2604 19.6632L25.6799 10.8547L21.7043 0L12.8428 11.4505ZM78.966 0.830078H83.0434V6.94602H87.2545V10.4687H83.0434V21.0011C83.0434 21.9304 83.6786 22.5619 84.5816 22.5619H87.2564V26.0846H83.5127C80.8039 26.0846 78.966 24.2895 78.966 21.5651V10.4668H75.7577V6.94414H78.966V0.830078ZM161.271 20.1897C161.272 20.1822 161.28 20.1654 161.28 20.1654V20.1635C161.38 19.9162 165.391 20.1635 165.391 20.1635C164.656 24.149 161.148 26.4743 156.937 26.4743C151.289 26.4743 147.547 22.1234 147.547 16.5097C147.547 10.8959 151.155 6.54504 156.569 6.54504C162.385 6.54504 165.961 11.1639 164.924 17.2086H151.657C151.857 20.4314 153.762 22.8224 156.937 22.8224C158.984 22.8224 160.569 21.8555 161.271 20.1897ZM160.946 13.8864C160.779 11.6944 159.075 10.1674 156.77 10.167C154.497 10.1674 152.594 11.4958 151.925 13.8864H160.946ZM12.8238 26.077V26.0827L12.8388 26.0658L12.8577 26.0827V26.077L18.2602 19.6651H7.42124L12.8238 26.077ZM96.9173 9.73422C95.0775 9.73422 93.841 10.598 93.841 11.8272C93.841 13.5311 95.821 13.9362 98.1307 14.4087C101.363 15.0701 105.242 15.8636 105.242 20.5345C105.242 24.0254 101.998 26.4837 97.6525 26.4837C93.3075 26.4837 89.7957 23.9242 89.1943 20.1354L93.2396 19.637C93.775 21.8968 95.7147 23.259 97.8203 23.259C99.7581 23.259 101.063 22.3296 101.063 20.9337C101.063 18.8577 98.862 18.4091 96.3974 17.9066C93.2605 17.267 89.6957 16.5402 89.6957 12.26C89.6957 8.87042 92.7043 6.54321 97.0512 6.54321C101.064 6.54321 104.508 8.90415 105.076 12.26L100.997 12.8578C100.495 10.9634 98.8891 9.73422 96.9173 9.73422ZM117.523 6.54688C115.115 6.54688 112.976 7.84351 111.807 10.0021L111.506 6.94599H107.962V32H112.007V23.3601C113.21 25.32 115.249 26.4836 117.523 26.4836C122.369 26.4836 125.847 22.5619 125.847 16.5153C125.847 10.4686 122.403 6.54688 117.523 6.54688ZM116.786 22.8279C113.945 22.8279 112.005 20.3696 112.005 16.5153C112.005 12.6609 113.945 10.2026 116.786 10.2026C119.626 10.2026 121.734 12.6272 121.734 16.5153C121.734 20.4033 119.762 22.8279 116.786 22.8279ZM128.697 6.94604H132.742V26.0846H128.697V6.94604ZM54.1119 0L45.6029 21.9323L37.1259 0H32.5433L42.1231 26.0827H49.0808L58.6586 0H54.1119ZM70.3534 20.1897C70.3544 20.186 70.3567 20.1801 70.3588 20.175L70.3629 20.1654V20.1635C70.4628 19.9162 74.4741 20.1635 74.4741 20.1635C73.739 24.149 70.2309 26.4743 66.0197 26.4743C60.3722 26.4743 56.6304 22.1234 56.6304 16.5097C56.6304 10.8959 60.2383 6.54504 65.6522 6.54504C71.4675 6.54504 75.0434 11.1639 74.0066 17.2086H60.7397C60.9396 20.4314 62.8453 22.8224 66.0197 22.8224C68.0669 22.8224 69.6522 21.8555 70.3534 20.1897ZM70.0292 13.8864C69.8615 11.6944 68.1578 10.1674 65.8529 10.167C63.58 10.1674 61.6765 11.4958 61.0074 13.8864H70.0292ZM140.628 10.034L140.294 6.94421H136.784V26.0865H140.829V16.0507C140.829 12.661 142.166 10.4669 144.874 10.4669H147.461V6.94421H144.607C142.635 6.94421 141.197 8.14154 140.628 10.034ZM128.343 2.256C128.343 0.920014 129.359 0 130.703 0C132.107 0 133.093 0.920014 133.093 2.256C133.093 3.59199 131.568 4.512 130.703 4.512C129.838 4.512 128.343 3.59199 128.343 2.256Z" fill="#4d65ff" />
            </svg>
            <span style={{ color: "#6b6b6b", fontWeight: "400", fontSize: "1.8rem" }}>Docs</span>
          </a>
        </div>
        <div
          style={{
            flexGrow: "1",
            overflowY: "scroll",
            overflowX: "hidden"
          }}
        >
          <div style={{ padding: "15px 20px", borderBottom: `1px solid ${darkMode ? "#30363d" : "#e5e7eb"}` }}>
            <button
              onClick={this.toggleSearch}
              style={{
                width: "100%",
                padding: "10px 12px",
                backgroundColor: darkMode ? "#21262d" : "#f9fafb",
                border: "1px solid",
                borderColor: darkMode ? "#30363d" : "#e5e7eb",
                borderRadius: "6px",
                fontSize: "1.4rem",
                color: darkMode ? "#c9d1d9" : "#6b6b6b",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = darkMode ? "#30363d" : "#f3f4f6";
                e.target.style.borderColor = "#4d65ff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = darkMode ? "#21262d" : "#f9fafb";
                e.target.style.borderColor = darkMode ? "#30363d" : "#e5e7eb";
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M11.5 7a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm-.82 4.74a6 6 0 111.06-1.06l3.04 3.04a.75.75 0 11-1.06 1.06l-3.04-3.04z" />
                </svg>
                Search...
              </span>
              <kbd style={{
                backgroundColor: darkMode ? "#161b22" : "#ffffff",
                padding: "2px 6px",
                borderRadius: "3px",
                border: "1px solid",
                borderColor: darkMode ? "#30363d" : "#e5e7eb",
                fontSize: "1.2rem"
              }}>⌘K</kbd>
            </button>
          </div>
          <div className="Sidebar-section">
            <div className="Sidebar-section-header">Guides</div>
            <Link to="/" className={currentPath === "/" && "active"}>
              Introduction
            </Link>
            <a target="_blank" href="http://graphql.org/learn/">
              GraphQL Guide
            </a>
          </div>

          <div style={{ padding: "15px 20px", borderTop: `1px solid ${darkMode ? "#30363d" : "#e5e7eb"}`, marginTop: "auto" }}>
            <button
              onClick={this.toggleDarkMode}
              style={{
                width: "100%",
                padding: "10px 12px",
                backgroundColor: darkMode ? "#30363d" : "#f9fafb",
                border: "1px solid",
                borderColor: darkMode ? "#30363d" : "#e5e7eb",
                borderRadius: "6px",
                fontSize: "1.4rem",
                color: darkMode ? "#c9d1d9" : "#6b6b6b",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = darkMode ? "#484f58" : "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = darkMode ? "#30363d" : "#f9fafb";
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {darkMode ? (
                  <React.Fragment>
                    <SunIcon />
                    Light Mode
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <MoonIcon />
                    Dark Mode
                  </React.Fragment>
                )}
              </span>
            </button>
          </div>

          <div style={{ borderTop: `2px solid ${darkMode ? "#30363d" : "#e5e7eb"}` }}>
            <div style={{
              padding: "8px 20px",
              fontSize: "1.1rem",
              fontWeight: "700",
              color: darkMode ? "#8b949e" : "#6b6b6b",
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}>
              API Reference
            </div>

            <SidebarSection
              title="Queries"
              icon={<MagnifyingGlassIcon />}
              count={totalQueries}
              collapsed={collapsedSections.queries}
              onToggle={() => this.toggleSection("queries")}
              items={
                contexts
                  .filter(ctx => queryGroups[ctx] && queryGroups[ctx].length > 0)
                  .sort()
                  .flatMap(ctx =>
                    queryGroups[ctx].map(node => (
                      <Link
                        key={node.path}
                        className={currentPath === node.path ? "active" : ""}
                        to={node.path}
                      >
                        {node.name}
                      </Link>
                    ))
                  )
              }
            />

            <SidebarSection
              title="Mutations"
              icon={<PencilSquareIcon />}
              count={totalMutations}
              collapsed={collapsedSections.mutations}
              onToggle={() => this.toggleSection("mutations")}
              items={
                contexts
                  .filter(ctx => mutationGroups[ctx] && mutationGroups[ctx].length > 0)
                  .sort()
                  .flatMap(ctx =>
                    mutationGroups[ctx].map(node => (
                      <Link
                        key={node.path}
                        className={currentPath === node.path ? "active" : ""}
                        to={node.path}
                      >
                        {node.name}
                      </Link>
                    ))
                  )
              }
            />
          </div>

          <div style={{ borderTop: `2px solid ${darkMode ? "#30363d" : "#e5e7eb"}` }}>
            <div style={{
              padding: "8px 20px",
              fontSize: "1.1rem",
              fontWeight: "700",
              color: darkMode ? "#8b949e" : "#6b6b6b",
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}>
              Type System
            </div>

            <SidebarSection
              title="Objects"
              icon={<CubeIcon />}
              count={countPagesByFilter(pages, node =>
                node.type === "OBJECT" && node.kind === "OBJECT" && node.name.substr(0, 2) !== "__"
              )}
              collapsed={collapsedSections.objects}
              onToggle={() => this.toggleSection("objects")}
              items={filterPagesByTypeAndKind(pages, "OBJECT", "OBJECT")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(node => (
                  <Link
                    key={node.path}
                    to={node.path}
                    className={currentPath === node.path && "active"}
                  >
                    {node.name}
                  </Link>
                ))
              }
            />

            {countPagesByFilter(pages, node =>
              node.type === "OBJECT" && node.kind === "UNION" && node.name.substr(0, 2) !== "__"
            ) > 0 && (
                <SidebarSection
                  title="Unions"
                  icon={<LinkIcon />}
                  count={countPagesByFilter(pages, node =>
                    node.type === "OBJECT" && node.kind === "UNION" && node.name.substr(0, 2) !== "__"
                  )}
                  collapsed={collapsedSections.unions}
                  onToggle={() => this.toggleSection("unions")}
                  items={filterPagesByTypeAndKind(pages, "OBJECT", "UNION")
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(node => (
                      <Link
                        key={node.path}
                        to={node.path}
                        className={currentPath === node.path && "active"}
                      >
                        {node.name}
                      </Link>
                    ))
                  }
                />
              )}

            <SidebarSection
              title="Input Objects"
              icon={<ArrowDownTrayIcon />}
              count={countPagesByFilter(pages, node =>
                node.type === "OBJECT" && node.kind === "INPUT_OBJECT" && node.name.substr(0, 2) !== "__"
              )}
              collapsed={collapsedSections.inputObjects}
              onToggle={() => this.toggleSection("inputObjects")}
              items={filterPagesByTypeAndKind(pages, "OBJECT", "INPUT_OBJECT")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(node => (
                  <Link
                    key={node.path}
                    to={node.path}
                    className={currentPath === node.path && "active"}
                  >
                    {node.name}
                  </Link>
                ))
              }
            />

            <SidebarSection
              title="Enums"
              icon={<TagIcon />}
              count={countPagesByFilter(pages, node =>
                node.type === "OBJECT" && node.kind === "ENUM" && node.name.substr(0, 2) !== "__"
              )}
              collapsed={collapsedSections.enums}
              onToggle={() => this.toggleSection("enums")}
              items={filterPagesByTypeAndKind(pages, "OBJECT", "ENUM")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(node => (
                  <Link
                    key={node.path}
                    to={node.path}
                    className={currentPath === node.path && "active"}
                  >
                    {node.name}
                  </Link>
                ))
              }
            />

            <SidebarSection
              title="Scalars"
              icon={<VariableIcon />}
              count={countPagesByFilter(pages, node =>
                node.type === "OBJECT" && node.kind === "SCALAR" && node.name.substr(0, 2) !== "__"
              )}
              collapsed={collapsedSections.scalars}
              onToggle={() => this.toggleSection("scalars")}
              items={filterPagesByTypeAndKind(pages, "OBJECT", "SCALAR")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(node => (
                  <Link
                    key={node.path}
                    to={node.path}
                    className={currentPath === node.path && "active"}
                  >
                    {node.name}
                  </Link>
                ))
              }
            />
          </div>
        </div>
        {showSearch && (
          <Search pages={pages} onClose={this.toggleSearch} />
        )}
      </div>
    );
  }
}

const Header = ({ currentPath }) => (
  <div
    className="Header"
    style={{
      width: "100%",
      height: HEADER_HEIGHT,
      position: "fixed",
      display: "flex",
      backgroundColor: "transparent"
    }}
  >
    <div
      className="Header-sidebar"
      style={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        flexShrink: "0"
      }}
    />
    <section
      style={{
        flexGrow: 1
      }}
    >
      <div className="section-text" />
      <div
        className={classnames("section-example Header-example", {
          on: currentPath === "/"
        })}
      />
    </section>
  </div>
);

// Utility function to count items by filter criteria
const countPagesByFilter = (pages, filterFn) => pages.filter(filterFn).length;

// Utility function to filter pages by type and kind
const filterPagesByTypeAndKind = (pages, type, kind) =>
  pages.filter(node =>
    node.type === type &&
    node.kind === kind &&
    node.name.substr(0, 2) !== "__"
  );

const TemplateWrapper = ({ children, layoutContext: { pages }, history }) => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Helmet>
        <title>Vetspire API Documentation</title>
        <meta charSet="utf-8" />
        <meta description="Vetspire API Documentation" />
        <link
          href="https://fonts.googleapis.com/css?family=Source+Sans+Pro:300,400,700"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css?family=Source+Code+Pro"
          rel="stylesheet"
        />
      </Helmet>
      <div
        style={{
          width: "100%",
          height: "100%"
        }}
      >
        <Header currentPath={history.location.pathname} />
        <Sidebar pages={pages} currentPath={history.location.pathname} />
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            paddingLeft: SIDEBAR_WIDTH,
            paddingTop: HEADER_HEIGHT,
            width: "100%",
            height: "100%"
          }}
        >
          {children()}
        </div>
      </div>
    </div>
  );
};

// PropTypes validation
SidebarSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.node.isRequired,
  currentPath: PropTypes.string.isRequired,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  icon: PropTypes.string,
  count: PropTypes.number
};

NestedSidebarSection.propTypes = {
  title: PropTypes.string.isRequired,
  contexts: PropTypes.arrayOf(PropTypes.string).isRequired,
  queryGroups: PropTypes.object.isRequired,
  mutationGroups: PropTypes.object.isRequired,
  currentPath: PropTypes.string.isRequired,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func.isRequired
};

Sidebar.propTypes = {
  pages: PropTypes.array.isRequired,
  currentPath: PropTypes.string.isRequired
};

Header.propTypes = {
  currentPath: PropTypes.string.isRequired
};

TemplateWrapper.propTypes = {
  children: PropTypes.func.isRequired,
  layoutContext: PropTypes.shape({
    pages: PropTypes.array.isRequired
  }).isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default TemplateWrapper;



// WEBPACK FOOTER //
// ./src/templates/layout.js